import { useState, type ChangeEvent, type FormEvent } from "react";
import type { AnimalItem } from "../../../../../mocks/animals";
import {
  getDisplayKupeNo,
  getDisplaySiparisNo,
} from "../../../../../lib/animalLabels";
import BackdropCloseContainer from "../../../../../components/ui/BackdropCloseContainer";

interface Props {
  animal: AnimalItem;
  onClose: () => void;
  onSave: (updated: Partial<AnimalItem>) => void;
}

export default function AnimalEditModal({ animal, onClose, onSave }: Props) {
  const [form, setForm] = useState({
    kupeNo: animal.kupeNo,
    tur: animal.tur,
    kg: animal.kg?.toString() ?? "",
    referansFiyat: animal.referansFiyat?.toString() ?? "",
    toplamHisse: animal.toplamHisse.toString(),
    durum: animal.durum,
    kesimGunu: animal.kesimGunu ?? "",
    hisseliSatisEtiketi: Boolean(animal.hisseliSatisEtiketi),
    disKesim: Boolean(animal.disKesim),
    defaultKesimUcreti: animal.defaultKesimUcreti?.toString() ?? "",
    defaultParcalamaUcreti: animal.defaultParcalamaUcreti?.toString() ?? "",
    defaultTasimaUcreti: animal.defaultTasimaUcreti?.toString() ?? "",
  });

  function handleChange(e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const target = e.target;

    if (target instanceof HTMLInputElement && target.type === "checkbox") {
      setForm((prev) => ({
        ...prev,
        [target.name]: target.checked,
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [target.name]: target.value,
    }));
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();

    onSave({
      kupeNo: form.kupeNo.trim(),
      tur: form.tur as AnimalItem["tur"],
      kg: form.kg ? Number(form.kg) : undefined,
      referansFiyat: form.referansFiyat
        ? Number(form.referansFiyat)
        : undefined,
      toplamHisse: Number(form.toplamHisse),
      durum: form.durum as AnimalItem["durum"],
      kesimGunu: form.kesimGunu || undefined,
      hisseliSatisEtiketi: form.hisseliSatisEtiketi,
      disKesim: form.disKesim,
      defaultKesimUcreti: form.defaultKesimUcreti
        ? Number(form.defaultKesimUcreti)
        : undefined,
      defaultParcalamaUcreti: form.defaultParcalamaUcreti
        ? Number(form.defaultParcalamaUcreti)
        : undefined,
      defaultTasimaUcreti: form.defaultTasimaUcreti
        ? Number(form.defaultTasimaUcreti)
        : undefined,
    });

    onClose();
  }

  return (
    <BackdropCloseContainer
      onClose={onClose}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-sm px-0 sm:px-4"
    >
      <div className="w-full sm:max-w-md bg-farm-warm border border-farm-border rounded-t-farm sm:rounded-farm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-farm-border bg-farm-cream">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center text-farm-earth">
              <i className="ri-edit-line text-base" />
            </div>

            <div className="min-w-0">
              <h2 className="font-heading font-semibold text-sm text-farm-brown">
                Hayvanı Düzenle
              </h2>
              <div className="flex items-center gap-2 flex-wrap mt-0.5">
                <span className="font-body text-[10px] text-farm-muted bg-farm-earth-pale px-2 py-0.5 rounded-full">
                  Küpe No: {getDisplayKupeNo(animal)}
                </span>
                <span className="font-body text-[10px] text-farm-muted bg-white px-2 py-0.5 rounded-full border border-farm-border">
                  Sipariş No: {getDisplaySiparisNo(animal)}
                </span>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center text-farm-muted hover:text-farm-brown rounded-full hover:bg-farm-cream transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="px-4 py-4 flex flex-col gap-3 max-h-[80vh] overflow-y-auto"
        >
          <p className="font-body text-[10px] font-semibold text-farm-muted uppercase tracking-wide">
            Temel Bilgiler
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="font-body text-xs font-semibold text-farm-brown" htmlFor="kupeNo">
                Sipariş No
              </label>
              <input
                id="kupeNo"
                name="kupeNo"
                type="text"
                value={form.kupeNo}
                onChange={handleChange}
                required
                className="px-3 py-2 rounded-md border border-farm-border bg-farm-cream font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="font-body text-xs font-semibold text-farm-brown" htmlFor="tur">
                Hayvan Türü
              </label>
              <select
                id="tur"
                name="tur"
                value={form.tur}
                onChange={handleChange}
                className="px-3 py-2 rounded-md border border-farm-border bg-farm-cream font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors cursor-pointer"
              >
                <option value="Büyükbaş">Büyükbaş</option>
                <option value="Küçükbaş">Küçükbaş</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="font-body text-xs font-semibold text-farm-brown" htmlFor="kg">
                Ağırlık (kg)
              </label>
              <input
                id="kg"
                name="kg"
                type="number"
                min="0"
                value={form.kg}
                onChange={handleChange}
                placeholder="390"
                className="px-3 py-2 rounded-md border border-farm-border bg-farm-cream font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="font-body text-xs font-semibold text-farm-brown" htmlFor="referansFiyat">
                Ref. Fiyat (₺/kg)
              </label>
              <input
                id="referansFiyat"
                name="referansFiyat"
                type="number"
                min="0"
                value={form.referansFiyat}
                onChange={handleChange}
                placeholder="155"
                className="px-3 py-2 rounded-md border border-farm-border bg-farm-cream font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="font-body text-xs font-semibold text-farm-brown" htmlFor="toplamHisse">
                Toplam Hisse
              </label>
              <input
                id="toplamHisse"
                name="toplamHisse"
                type="number"
                min="1"
                max="14"
                value={form.toplamHisse}
                onChange={handleChange}
                required
                className="px-3 py-2 rounded-md border border-farm-border bg-farm-cream font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="font-body text-xs font-semibold text-farm-brown" htmlFor="durum">
                Durum
              </label>
              <select
                id="durum"
                name="durum"
                value={form.durum}
                onChange={handleChange}
                className="px-3 py-2 rounded-md border border-farm-border bg-farm-cream font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors cursor-pointer"
              >
                <option value="Aktif">Aktif</option>
                <option value="Kesim Bekliyor">Kesim Bekliyor</option>
                <option value="Kesildi">Kesildi</option>
                <option value="Teslim Edildi">Teslim Edildi</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="font-body text-xs font-semibold text-farm-brown" htmlFor="kesimGunu">
                Kesim Günü
              </label>
              <input
                id="kesimGunu"
                name="kesimGunu"
                type="date"
                value={form.kesimGunu}
                onChange={handleChange}
                className="px-3 py-2 rounded-md border border-farm-border bg-farm-cream font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="w-full flex items-center gap-2 px-3 py-2 rounded-md border border-farm-border bg-farm-cream cursor-pointer">
                <input
                  type="checkbox"
                  name="hisseliSatisEtiketi"
                  checked={form.hisseliSatisEtiketi}
                  onChange={handleChange}
                  className="accent-[#8b5e3c]"
                />
                <span className="font-body text-xs font-semibold text-farm-brown">
                  Hisseli Satış
                </span>
              </label>

              <label className="w-full flex items-center gap-2 px-3 py-2 rounded-md border border-farm-border bg-farm-cream cursor-pointer">
                <input
                  type="checkbox"
                  name="disKesim"
                  checked={form.disKesim}
                  onChange={handleChange}
                  className="accent-[#8b5e3c]"
                />
                <span className="font-body text-xs font-semibold text-farm-brown">
                  Dış Kesim
                </span>
              </label>
            </div>
          </div>

          <p className="font-body text-[10px] font-semibold text-farm-muted uppercase tracking-wide mt-1">
            Varsayılan Hizmet Ücretleri
          </p>

          <div className="grid grid-cols-3 gap-3">
            <div className="flex flex-col gap-1">
              <label className="font-body text-xs font-semibold text-farm-brown" htmlFor="defaultKesimUcreti">
                Kesim (₺)
              </label>
              <input
                id="defaultKesimUcreti"
                name="defaultKesimUcreti"
                type="number"
                min="0"
                value={form.defaultKesimUcreti}
                onChange={handleChange}
                placeholder="0"
                className="px-3 py-2 rounded-md border border-farm-border bg-farm-cream font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="font-body text-xs font-semibold text-farm-brown" htmlFor="defaultParcalamaUcreti">
                Parçalama (₺)
              </label>
              <input
                id="defaultParcalamaUcreti"
                name="defaultParcalamaUcreti"
                type="number"
                min="0"
                value={form.defaultParcalamaUcreti}
                onChange={handleChange}
                placeholder="0"
                className="px-3 py-2 rounded-md border border-farm-border bg-farm-cream font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="font-body text-xs font-semibold text-farm-brown" htmlFor="defaultTasimaUcreti">
                Taşıma (₺)
              </label>
              <input
                id="defaultTasimaUcreti"
                name="defaultTasimaUcreti"
                type="number"
                min="0"
                value={form.defaultTasimaUcreti}
                onChange={handleChange}
                placeholder="0"
                className="px-3 py-2 rounded-md border border-farm-border bg-farm-cream font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors"
              />
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 rounded-full border border-farm-border text-farm-muted font-body text-xs font-semibold cursor-pointer whitespace-nowrap"
            >
              İptal
            </button>
            <button
              type="submit"
              className="flex-1 py-2 rounded-full bg-farm-earth text-white font-body text-xs font-semibold hover:bg-farm-brown transition-colors cursor-pointer whitespace-nowrap"
            >
              Kaydet
            </button>
          </div>
        </form>
      </div>
    </BackdropCloseContainer>
  );
}
